import { Outlet, useLocation, useNavigate, useParams } from "react-router-dom";
import {
  WebAppAiChatUrl,
  WebAppBuildHistoryUrl,
  WebAppEnvUrl,
  WebAppEventsUrl,
  WebAppLogsUrl,
  WebAppMetricsUrl,
  WebAppScalingUrl,
  WebAppSettingsUrl,
  WebAppShellUrl,
  WebAppSourceCodeUrl,
  WebAppUrl,
  WebAppsUrl,
} from "../../utils/urls";
import { useEffect, useMemo, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../hooks";
import RightArrow from "./../../assets/icons/arrow-right.svg?react";
import { Helmet } from "react-helmet";
import {
  getWebAppAsync,
  getWebAppOverviewAsync,
  handleClearWebAppSlice,
  selectWebApp,
  selectWebAppLoading,
} from "../../store/web-app/webAppSlice";
import { NotExist, WebAppNotActiveTab } from "../../components/layouts/NotData";
import ComputerIcon from "./../../assets/icons/computer-desktop.svg?react";
import ListBulletIcon from "./../../assets/icons/list-bullet.svg?react";
import EnviromentIcon from "./../../assets/icons/globe-alt.svg?react";
import ShellIcon from "./../../assets/icons/shell.svg?react";
import MetricsIcon from "./../../assets/icons/adjustments-vertical.svg?react";
import ScaleIcon from "./../../assets/icons/scale.svg?react";
import SettingIcon from "./../../assets/icons/setting.svg?react";
import BeatIcon from "./../../assets/icons/beat.svg?react";
import Calender from "./../../assets/icons/calendar.svg?react";
import ChatIcon from "./../../assets/icons/editor-files/chat-bubble.svg?react";
import ClockIcon from "./../../assets/icons/clock.svg?react";
import {
  Skeleton,
  Typography,
  Tabs,
  TabOptions,
  Flex,
  Alert,
} from "djuno-design";
import { getWebAppsPlansAsync } from "../../store/web-app/webAppsSlice";
import { WebAppStatusTag } from "./WebAppsPage";
import { ServiceRefreshStatus } from "../../store/publicSlice";

const WebAppSinglePage = () => {
  const { webAppId } = useParams();
  const webApp = useAppSelector(selectWebApp);
  const webAppLoading = useAppSelector(selectWebAppLoading);

  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [isExist, setIsExist] = useState<boolean>(true);

  // get web app data
  useEffect(() => {
    if ((webApp === null || webApp.Id.toString() !== webAppId) && webAppId) {
      dispatch(
        getWebAppAsync({
          webAppId,
        })
      ).then((action) => {
        if (action.type === "web-app/rejected") {
          setIsExist(false);
        }
      });
    }
  }, [dispatch, webAppId, webApp]);

  const isActive = useMemo(() => {
    if (!webApp) return true;
    return !ServiceRefreshStatus.includes(webApp.ServiceStatus);
  }, [webApp]);

  // get web app overview data
  useEffect(() => {
    if (webApp && isActive)
      dispatch(
        getWebAppOverviewAsync({
          webAppId: webApp.Id.toString(),
          withoutLoading: false,
        })
      );
  }, [dispatch, isActive, webApp]);

  // refresh web app data based on app status
  useEffect(() => {
    if (webApp) {
      let interval: NodeJS.Timeout | undefined;
      const intervalTime = 8000;

      const shouldSetInterval = ServiceRefreshStatus.includes(
        webApp.ServiceStatus
      );

      if (shouldSetInterval) {
        interval = setInterval(() => {
          dispatch(
            getWebAppAsync({
              webAppId: webApp.Id.toString(),
              withoutLoading: true,
            })
          );
        }, intervalTime);
      } else {
        if (interval) clearInterval(interval);
      }

      return () => {
        if (interval) {
          clearInterval(interval);
        }
      };
    }
  }, [dispatch, webAppId, webApp]);

  // cleanup
  useEffect(() => {
    return () => {
      dispatch(handleClearWebAppSlice());
    };
  }, [dispatch]);

  // get web app plans
  useEffect(() => {
    dispatch(getWebAppsPlansAsync({}));
  }, [dispatch]);

  const Web3EndpointPageMenus: TabOptions = useMemo(
    () => [
      {
        label: (
          <div className="flex items-center gap-1 ">
            <ComputerIcon className="w-3" />
            Overview
          </div>
        ),
        url: WebAppUrl(webAppId),
      },
      {
        label: (
          <div className="flex items-center gap-1 logs">
            <BeatIcon className="w-3" />
            Logs
          </div>
        ),
        url: WebAppLogsUrl(webAppId),
      },
      {
        label: (
          <div className="flex items-center gap-1 environment">
            <EnviromentIcon className="w-3" />
            Environment
          </div>
        ),

        url: WebAppEnvUrl(webAppId),
      },
      {
        label: (
          <div className="flex items-center gap-1 sell">
            <ShellIcon className="w-3" />
            Shell
          </div>
        ),

        url: WebAppShellUrl(webAppId),
      },
      {
        label: (
          <div className="flex items-center gap-1 metrics">
            <MetricsIcon className="w-3" />
            Metrics
          </div>
        ),
        url: WebAppMetricsUrl(webAppId),
      },
      {
        label: (
          <div className="flex items-center gap-1 scaling">
            <ScaleIcon className="w-3" />
            Scaling
          </div>
        ),
        url: WebAppScalingUrl(webAppId),
      },
      {
        label: (
          <div className="flex items-center gap-1 events">
            <Calender className="w-3" />
            Events
          </div>
        ),
        url: WebAppEventsUrl(webAppId),
      },
      {
        label: (
          <div className="flex items-center gap-1 settings">
            <SettingIcon className="w-3" />
            Settings
          </div>
        ),
        url: WebAppSettingsUrl(webAppId),
      },
      // {
      //   label: (
      //     <div className="flex items-center gap-1">
      //       <ClockIcon className="w-3" />
      //       Build History
      //     </div>
      //   ),
      //   url: WebAppBuildHistoryUrl(webAppId),
      //   isVisible: false,
      // },
      {
        label: (
          <div className="flex items-center gap-1">
            <ListBulletIcon className="w-3" />
            Source Code
          </div>
        ),
        url: WebAppSourceCodeUrl(webAppId),
        disabled: !webApp?.IsSmart,
        isVisible: webApp === null ? false : webApp.IsSmart,
      },
      {
        label: (
          <div className="flex items-center gap-1">
            <ChatIcon className="w-3" />
            AI Chat
          </div>
        ),
        url: WebAppAiChatUrl(webAppId),
        disabled: !webApp?.IsSmart,
        isVisible: webApp === null ? false : webApp.IsSmart,
      },
    ],
    [webApp, webAppId]
  );

  const pageName = useMemo(
    () => location.pathname.split("/").filter(Boolean)[2] || "",
    [location.pathname]
  );

  return (
    <>
      <Helmet>
        <title>
          {import.meta.env.VITE_APP_NAME} | Web App ({webApp?.ServiceName || ""}
          ) {pageName}
        </title>
        <meta
          name="description"
          content="web3-auth refers to authentication mechanisms used within Web3 applications, which are decentralized applications built on blockchain technology. Web3-auth facilitates user authentication and authorization processes within these decentralized applications"
        />
      </Helmet>
      <Flex items="center" justify="between" className="h-20">
        <Flex items="center" justify="between" className="px-6 flex-1">
          <Flex items="center" className="gap-2">
            <RightArrow
              onClick={() => navigate(WebAppsUrl)}
              className="rotate-180 w-5 h-5 hover:scale-110 transition-all duration-500 text-slate-800 dark:text-slate-100 cursor-pointer"
            />
            {webAppLoading ? (
              <Skeleton shape="rectangle" className="!w-48" />
            ) : (
              <Flex items="center" className="gap-2">
                <Typography.Title
                  level={5}
                  className="!mb-0 !whitespace-nowrap !max-w-[200px] md:!max-w-[400px] lg:!max-w-[350px] xl:!max-w-[400px] !truncate"
                >
                  {webApp?.ServiceName}
                </Typography.Title>
                <WebAppStatusTag
                  ServiceStatus={webApp?.ServiceStatus}
                  IsSuspended={webApp?.IsSuspended}
                />
              </Flex>
            )}
          </Flex>
        </Flex>
        <div className="mr-6 w-96"></div>
      </Flex>
      {isExist ? (
        <div className="mx-6">
          <Tabs
            options={Web3EndpointPageMenus.map((o) => ({
              label: o.label,
              url: o.url,
              disabled: o.disabled,
              isVisible: o.isVisible,
            }))}
          />
          {webApp &&
            webApp.IsSuspended &&
            !ServiceRefreshStatus.includes(webApp.ServiceStatus) && (
              <Alert uiType="warning" className="mt-5">
                <Typography.Text size="sm">
                  Your web app is suspended. To use it again, please update your
                  settings and resume from the Settings tab.
                </Typography.Text>
              </Alert>
            )}
          {webApp && isActive && (
            <div className="py-6 pb-3">
              <Outlet />
            </div>
          )}
        </div>
      ) : (
        <NotExist url={() => navigate(WebAppsUrl)} />
      )}
      {!webAppLoading && webApp && !isActive && (
        <WebAppNotActiveTab nodeStatus={webApp.ServiceStatus} />
      )}
    </>
  );
};
export default WebAppSinglePage;
